import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { authAPI } from '@/utils/api';
import { toast } from 'sonner';
import { Sparkles, ArrowLeft } from 'lucide-react';

const Login = () => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({ email: '', password: '' });
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    
    try {
      const response = await authAPI.login(formData);
      localStorage.setItem('token', response.data.token);
      localStorage.setItem('user', JSON.stringify(response.data.user));
      toast.success('Login successful!');
      
      // Redirect based on role
      const role = response.data.user.role;
      if (role === 'admin') navigate('/admin');
      else if (role === 'brand') navigate('/brand/dashboard');
      else navigate('/products');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Login failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#FAF8F5] flex items-center justify-center px-6">
      <div className="w-full max-w-md">
        <Button
          data-testid="back-btn"
          variant="ghost"
          onClick={() => navigate('/')}
          className="mb-6 text-[#5A6C7D] hover:text-[#1A3A52] hover:bg-[#F5F3F0]"
        >
          <ArrowLeft className="mr-2 h-4 w-4" />
          Back
        </Button>
        
        <div className="bg-white border border-[#E5E1DC] rounded-2xl p-10 shadow-lg">
          <div className="flex items-center justify-center space-x-2 mb-8">
            <Sparkles className="h-8 w-8 text-[#1A3A52]" strokeWidth={1.5} />
            <span className="text-3xl font-serif font-semibold text-[#2C3E50]">LuxLyx</span>
          </div>
          
          <h2 className="text-2xl font-serif font-semibold mb-2 text-center text-[#2C3E50]">Welcome Back</h2>
          <p className="text-center text-[#5A6C7D] mb-8">Sign in to your account</p>
          
          <form onSubmit={handleSubmit} className="space-y-6">
            <div>
              <Label htmlFor="email" className="text-[#2C3E50] font-medium">Email</Label>
              <Input
                id="email"
                data-testid="email-input"
                type="email"
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                required
                className="mt-2 bg-[#FAF8F5] border-[#E5E1DC] text-[#2C3E50] focus:border-[#1A3A52]"
              />
            </div>
            
            <div>
              <Label htmlFor="password" className="text-[#2C3E50] font-medium">Password</Label>
              <Input
                id="password"
                data-testid="password-input"
                type="password"
                value={formData.password}
                onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                required
                className="mt-2 bg-[#FAF8F5] border-[#E5E1DC] text-[#2C3E50] focus:border-[#1A3A52]"
              />
            </div>
            
            <Button
              data-testid="login-submit-btn"
              type="submit"
              disabled={loading}
              className="w-full bg-[#1A3A52] text-white hover:bg-[#2D4F6B] font-medium py-6"
            >
              {loading ? 'Signing in...' : 'Sign In'}
            </Button>
          </form>
          
          <p className="text-center text-[#5A6C7D] mt-6">
            Don't have an account?{' '}
            <button
              data-testid="register-link"
              onClick={() => navigate('/register')}
              className="text-[#1A3A52] hover:underline font-medium"
            >
              Sign up
            </button>
          </p>
        </div>
      </div>
    </div>
  );
};

export default Login;
